import { FC, JSX } from 'react';
import Box from '@mui/joy/Box';
import Chip from '@mui/joy/Chip';
import Typography from '@mui/joy/Typography';
import { ThemeProvider as JoyThemeProvider } from '@mui/joy/styles';
import type { ColorPaletteProp } from '@mui/joy/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import './StatusChips.scss';

export type StatusChipOption = {
  value: string;
  /** Translated. */
  label: string;
  color: ColorPaletteProp;
};

export type StatusChipsProps = {
  /** Translated caption above the chips. */
  label: string;
  options: StatusChipOption[];
  /** The picked option's value, or `''` for none. */
  value: string;
  /** Called with the picked value, or `''` when the picked chip is clicked again. */
  onChange: (value: string) => void;
};

/**
 * A single choice shown as a row of chips, each marked with its colour: every
 * option in view and one click away, the picked one filled in its colour.
 */
export const StatusChips: FC<StatusChipsProps> = ({
  label,
  options,
  value,
  onChange,
}): JSX.Element => (
  // The filters menu renders under the explorer's Material ThemeProvider, which
  // stands in for the Joy theme, and a Joy component there throws. Joy's
  // ThemeProvider puts a Joy theme back; its values are the `--joy-*` variables
  // the app's CssVarsProvider sets, so the font and colour scheme still apply.
  <JoyThemeProvider>
    <Box className="status-chips">
      <Typography
        level="body-xs"
        fontWeight={400}
        textColor="text.secondary"
        className="status-chips__label"
      >
        {label}
      </Typography>
      <Box className="status-chips__options">
        {options.map((option) => {
          const picked = option.value === value;
          return (
            <Chip
              key={option.value}
              size="sm"
              variant={picked ? 'soft' : 'outlined'}
              color={picked ? option.color : 'neutral'}
              startDecorator={
                <span
                  aria-hidden="true"
                  className="status-chips__dot"
                  style={{
                    backgroundColor: `var(--joy-palette-${option.color}-500)`,
                  }}
                />
              }
              endDecorator={
                picked ? <CloseRoundedIcon aria-hidden="true" /> : undefined
              }
              onClick={() => onChange(picked ? '' : option.value)}
              slotProps={{
                action: { 'aria-label': option.label, 'aria-pressed': picked },
              }}
            >
              {option.label}
            </Chip>
          );
        })}
      </Box>
    </Box>
  </JoyThemeProvider>
);
